import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, FileText } from "lucide-react";
import { WhatsAppGlyph } from "@/components/cards/CardShell";
import PublicHome from "./PublicHome";
import PublicMenu from "./PublicMenu";
import PriceList from "./PriceList";
import ResaleList from "./ResaleList";
import Offers from "./Offers";
import Profit from "./Profit";
import { waLink } from "@/lib/wa";

const VIEWS = { lista: PriceList, sugestie: ResaleList, oferte: Offers, profit: Profit };

const useSection = () => {
  const read = () => (VIEWS[window.location.hash.slice(1)] ? window.location.hash.slice(1) : "home");
  const [sec, setSec] = useState(read);
  useEffect(() => {
    const on = () => setSec(read());
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  const go = (id) => {
    window.location.hash = id === "home" ? "" : id;
    window.scrollTo({ top: 0 });
  };
  return [sec, go];
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const visitorId = () => {
  let id = localStorage.getItem("mbe_vid");
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("mbe_vid", id);
  }
  return id;
};


const useCardWidth = () => {
  const calc = () => Math.min(window.innerWidth - 24, 460);
  const [w, setW] = useState(calc);
  useEffect(() => {
    const on = () => setW(calc());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return w;
};

export default function PublicCatalog() {
  const { slug } = useParams();
  const [cat, setCat] = useState(null);
  const [missing, setMissing] = useState(false);
  const width = useCardWidth();
  const [sec, go] = useSection();
  const params = new URLSearchParams(window.location.search);
  const preview = params.has("preview");
  const offerId = params.get("o");
  const track = useCallback(
    (kind, target, items) => (preview ? null : axios.post(`${API}/public/${slug}/event`, { visitor_id: visitorId(), kind, target, items, offer_id: offerId }).catch(() => {})),
    [slug, preview, offerId]
  );
  useEffect(() => {
    if (sec !== "home") track("section", sec);
  }, [sec, track]);
  const offerPack = cat?.offer?.pack_id || (cat && params.get("oferta"));
  useEffect(() => {
    if (offerPack) track("offer", offerPack);
  }, [offerPack, track]);

  useEffect(() => {
    const offer = offerId ? axios.get(`${API}/public/${slug}/offer/${offerId}`).then((r) => r.data).catch(() => null) : null;
    Promise.all([axios.get(`${API}/public/${slug}`), offer]).then(([r, o]) => setCat({ ...r.data, offer: o })).catch(() => setMissing(true));
    const key = `mbe_visit_${slug}`;
    if (!preview && !sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1");
      axios.post(`${API}/public/${slug}/visit`, { visitor_id: visitorId() }).catch(() => {});
    }
  }, [slug, preview, offerId]);

  const data = useMemo(() => {
    if (!cat) return null;
    const abs = (u) => (u ? `${BACKEND}${u}` : null);
    return { ...cat, cover: { ...cat.cover, image: abs(cat.cover.image) }, products: cat.products.map((p) => ({ ...p, image: abs(p.image) })) };
  }, [cat]);

  if (missing) return <div className="st-loading" data-testid="public-missing">Linkul nu mai este valid.</div>;
  if (!data) return <div className="st-loading" data-testid="public-loading"><Loader2 className="animate-spin" /> Se încarcă oferta…</div>;

  const agent = data.contact.salon || "Agent Medavita";
  const wa = waLink(data.contact.whatsapp);
  const View = VIEWS[sec];

  return (
    <div className="pub-app" data-testid="public-catalog">
      {preview && <div className="pub-preview" data-testid="public-preview-banner">Previzualizare · exact ce vede clientul · nu se contorizează în statistici</div>}
      <header className="pub-top">
        <button type="button" className="pub-brand" onClick={() => go("home")} data-testid="public-home-btn">
          <span className="st-brand-mark">Instant <em>Bond Repair</em></span>
          <span className="pub-agent" data-testid="public-agent-name">Ofertă de la <b>{agent}</b></span>
          {data.contact.whatsapp && <span className="pub-agent pub-phone" data-testid="public-agent-phone">Tel. {data.contact.whatsapp}</span>}
          {data.offer && <span className="pub-for" data-testid="public-offer-for">Ofertă pentru <b>{data.offer.salon}</b></span>}
        </button>
        <PublicMenu sec={sec} go={go} />
      </header>
      <main className="pub-main" key={sec}>
        {View ? <View data={data} width={width} track={track} /> : <PublicHome data={data} width={width} />}
      </main>
      <nav className="pub-bar">
        {wa && (
          <a className="st-btn pub-wa" href={wa} target="_blank" rel="noopener noreferrer" data-testid="public-whatsapp-btn">
            <WhatsAppGlyph /> Scrie-i lui {agent.split(" ")[0]}
          </a>
        )}
        <a className="st-btn st-btn--silver" href={`${API}/public/${slug}/pdf${preview ? "?preview=1" : ""}`} data-testid="public-pdf-btn">
          <FileText size={16} /> Listă prețuri PDF
        </a>
      </nav>
    </div>
  );
}
